import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  message: ReactNode;
  /** Bold first line. Optional — a one-line toast reads better without it. */
  title?: string;
  variant?: ToastVariant;
  /** Milliseconds before auto-dismiss. 0 keeps it until dismissed. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastRecord extends Required<Pick<ToastOptions, 'message' | 'variant' | 'duration'>> {
  id: string;
  title?: string;
  action?: { label: string; onClick: () => void };
}

export interface ToastApi {
  /** Full control. Returns the id so you can dismiss it early. */
  toast: (options: ToastOptions) => string;
  success: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  error: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  warning: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  info: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export const TOAST_DEFAULT_DURATION_MS = 4000;

const VARIANT_ICON: Record<ToastVariant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

const VARIANT_CLASS: Record<ToastVariant, string> = {
  info: 'border-info/40 text-info',
  success: 'border-gain/40 text-gain',
  warning: 'border-warning/40 text-warning',
  danger: 'border-loss/40 text-loss',
};

const VARIANT_PREFIX: Record<ToastVariant, string> = {
  info: 'Aviso',
  success: 'Sucesso',
  warning: 'Atenção',
  danger: 'Erro',
};

export interface ToastProviderProps {
  children: ReactNode;
  /** Cap on simultaneously visible toasts; the oldest is dropped past this. */
  max?: number;
}

/**
 * Toasts are announced through a polite live region and never take focus —
 * a background upload finishing must not steal the keyboard from a form.
 */
export function ToastProvider({ children, max = 4 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current.clear();
    setToasts([]);
  }, []);

  const push = useCallback(
    (options: ToastOptions): string => {
      const id = `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const record: ToastRecord = {
        id,
        message: options.message,
        title: options.title,
        variant: options.variant ?? 'info',
        duration: options.duration ?? TOAST_DEFAULT_DURATION_MS,
        action: options.action,
      };
      setToasts((current) => [...current, record].slice(-max));
      if (record.duration > 0) {
        timers.current.set(
          id,
          window.setTimeout(() => {
            timers.current.delete(id);
            setToasts((current) => current.filter((item) => item.id !== id));
          }, record.duration),
        );
      }
      return id;
    },
    [max],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast: push,
      success: (message, options) => push({ ...options, message, variant: 'success' }),
      error: (message, options) => push({ ...options, message, variant: 'danger' }),
      warning: (message, options) => push({ ...options, message, variant: 'warning' }),
      info: (message, options) => push({ ...options, message, variant: 'info' }),
      dismiss,
      dismissAll,
    }),
    [push, dismiss, dismissAll],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(<ToastViewport toasts={toasts} onDismiss={dismiss} />, document.body)
        : null}
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastRecord[]; onDismiss: (id: string) => void }) {
  return (
    <div
      role="region"
      aria-label="Notificações"
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4',
        'pb-[calc(var(--height-bottom-nav)+env(safe-area-inset-bottom)+1rem)]',
        'lg:bottom-auto lg:right-0 lg:top-0 lg:items-end lg:pb-4 lg:pt-[calc(var(--height-header)+1rem)]',
      )}
    >
      <div aria-live="polite" aria-atomic="false" className="contents">
        {toasts.map((item) => {
          const Icon = VARIANT_ICON[item.variant];
          return (
            <div
              key={item.id}
              className={cn(
                'anim-slide-in-right pointer-events-auto flex w-full max-w-sm items-start gap-3',
                'rounded-lg border bg-surface-2 p-3 shadow-overlay',
                VARIANT_CLASS[item.variant],
              )}
            >
              <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />

              <div className="min-w-0 flex-1 text-text">
                <span className="sr-only">{VARIANT_PREFIX[item.variant]}: </span>
                {item.title ? <p className="text-base font-semibold leading-tight">{item.title}</p> : null}
                <div className={cn('text-base leading-snug', item.title && 'mt-0.5 text-text-muted')}>
                  {item.message}
                </div>
                {item.action ? (
                  <button
                    type="button"
                    onClick={() => {
                      item.action?.onClick();
                      onDismiss(item.id);
                    }}
                    className="mt-2 min-h-11 text-sm font-semibold text-primary underline underline-offset-4"
                  >
                    {item.action.label}
                  </button>
                ) : null}
              </div>

              <button
                type="button"
                onClick={() => onDismiss(item.id)}
                aria-label="Dispensar notificação"
                className={cn(
                  'flex size-11 shrink-0 items-center justify-center rounded-md text-text-muted',
                  'transition-colors duration-150 ease-out-soft hover:bg-surface-3 hover:text-text',
                )}
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast precisa estar dentro de <ToastProvider>.');
  return context;
}
